import type { StepsBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';

interface StepsInspectorProps {
  block: StepsBlock;
  path: DragPath;
}

export function StepsInspector({ block, path }: StepsInspectorProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);

  return (
    <div className="space-y-6">
      {/* Steps configuration */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="field-label font-medium">Steps</label>
          <button
            type="button"
            className="text-xs font-semibold text-accent hover:text-ink"
            onClick={() => {
              const newSteps = [
                ...(block.attrs?.steps || []),
                { title: 'New Step', description: '' },
              ];
              updateBlock(
                block.id,
                {
                  attrs: { ...block.attrs, steps: newSteps },
                },
                path,
              );
            }}
          >
            + Add Step
          </button>
        </div>

        {!block.attrs?.steps || block.attrs.steps.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-bg-soft p-6 text-center text-sm text-muted">
            <p>No steps added yet</p>
            <button
              type="button"
              className="mt-2 text-accent hover:text-primary"
              onClick={() => {
                updateBlock(
                  block.id,
                  {
                    attrs: { ...block.attrs, steps: [{ title: 'Step 1', description: '' }] },
                  },
                  path,
                );
              }}
            >
              Add your first step
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {block.attrs.steps.map((step, index) => (
              <div key={index} className="rounded-lg border border-line p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-ink-soft">Step {index + 1}</span>
                  <button
                    type="button"
                    className="text-sm text-danger hover:text-danger"
                    onClick={() => {
                      const newSteps = block.attrs.steps.filter((_, i) => i !== index);
                      updateBlock(
                        block.id,
                        {
                          attrs: { ...block.attrs, steps: newSteps },
                        },
                        path,
                      );
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="field-label text-xs">Title</label>
                    <input
                      type="text"
                      className="field-input"
                      placeholder="Step title"
                      value={step.title || ''}
                      onChange={(e) => {
                        const newSteps = [...block.attrs.steps];
                        newSteps[index] = { ...step, title: e.target.value };
                        updateBlock(
                          block.id,
                          {
                            attrs: { ...block.attrs, steps: newSteps },
                          },
                          path,
                        );
                      }}
                    />
                  </div>

                  <div>
                    <label className="field-label text-xs">Description</label>
                    <textarea
                      className="field-input min-h-[60px]"
                      placeholder="Step description (optional)"
                      value={step.description || ''}
                      onChange={(e) => {
                        const newSteps = [...block.attrs.steps];
                        newSteps[index] = { ...step, description: e.target.value };
                        updateBlock(
                          block.id,
                          {
                            attrs: { ...block.attrs, steps: newSteps },
                          },
                          path,
                        );
                      }}
                    />
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="field-label text-xs">Icon (emoji or icon name)</label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="🚀 or check-circle"
                        value={step.icon || ''}
                        onChange={(e) => {
                          const newSteps = [...block.attrs.steps];
                          newSteps[index] = { ...step, icon: e.target.value };
                          updateBlock(
                            block.id,
                            {
                              attrs: { ...block.attrs, steps: newSteps },
                            },
                            path,
                          );
                        }}
                      />
                    </div>

                    <div>
                      <label className="field-label text-xs">CTA Text (optional)</label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="Learn more"
                        value={step.cta?.text || ''}
                        onChange={(e) => {
                          const newSteps = [...block.attrs.steps];
                          newSteps[index] = {
                            ...step,
                            cta: { ...step.cta, text: e.target.value },
                          };
                          updateBlock(
                            block.id,
                            {
                              attrs: { ...block.attrs, steps: newSteps },
                            },
                            path,
                          );
                        }}
                      />
                    </div>
                  </div>

                  {step.cta?.text && (
                    <div>
                      <label className="field-label text-xs">CTA URL</label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="https://example.com"
                        value={step.cta?.url || ''}
                        onChange={(e) => {
                          const newSteps = [...block.attrs.steps];
                          newSteps[index] = {
                            ...step,
                            cta: { ...step.cta, url: e.target.value },
                          };
                          updateBlock(
                            block.id,
                            {
                              attrs: { ...block.attrs, steps: newSteps },
                            },
                            path,
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Layout settings */}
      <div className="space-y-4 border-t border-line pt-4">
        <h4 className="text-sm font-medium text-ink-soft">Layout Settings</h4>

        <div>
          <label className="field-label">Layout</label>
          <select
            className="field-input"
            value={block.attrs?.layout || 'vertical'}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: { ...block.attrs, layout: e.target.value },
                },
                path,
              )
            }
          >
            <option value="vertical">Vertical</option>
            <option value="horizontal">Horizontal</option>
            <option value="alternating">Alternating</option>
          </select>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="field-label">Step Size</label>
            <select
              className="field-input"
              value={block.attrs?.stepSize || 'md'}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    attrs: { ...block.attrs, stepSize: e.target.value },
                  },
                  path,
                )
              }
            >
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
            </select>
          </div>

          <div>
            <label className="field-label">Connector Type</label>
            <select
              className="field-input"
              value={block.attrs?.connectorType || 'line'}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    attrs: { ...block.attrs, connectorType: e.target.value },
                  },
                  path,
                )
              }
            >
              <option value="line">Line</option>
              <option value="arrow">Arrow</option>
              <option value="dashed">Dashed</option>
            </select>
          </div>
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={block.attrs?.showNumbers !== false}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    attrs: { ...block.attrs, showNumbers: e.target.checked },
                  },
                  path,
                )
              }
            />
            Show step numbers
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={block.attrs?.showConnectors !== false}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    attrs: { ...block.attrs, showConnectors: e.target.checked },
                  },
                  path,
                )
              }
            />
            Show connectors
          </label>
        </div>

        {/* Per-block color overrides for the steps' internal parts */}
        <div className="space-y-3 border-t border-line pt-4">
          <label className="field-label font-medium">Colors</label>
          <p className="text-xs text-muted">Leave empty to use the theme/design tokens.</p>
          {(
            [
              ['accent', 'Accent (numbers & CTA)'],
              ['indicatorBg', 'Icon background'],
              ['connector', 'Connector lines'],
              ['title', 'Step titles'],
              ['description', 'Step descriptions'],
            ] as const
          ).map(([key, label]) => {
            const value = block.attrs?.colors?.[key] ?? '';
            return (
              <div key={key} className="flex items-center gap-3">
                <input
                  type="color"
                  className="h-9 w-9 shrink-0 cursor-pointer rounded-lg border border-line-strong bg-bg-soft p-1"
                  value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
                  onChange={(e) =>
                    updateBlock(
                      block.id,
                      {
                        attrs: {
                          ...block.attrs,
                          colors: { ...(block.attrs?.colors ?? {}), [key]: e.target.value },
                        },
                      },
                      path,
                    )
                  }
                />
                <input
                  type="text"
                  className="field-input font-mono text-xs"
                  placeholder={label}
                  value={value}
                  onChange={(e) =>
                    updateBlock(
                      block.id,
                      {
                        attrs: {
                          ...block.attrs,
                          colors: {
                            ...(block.attrs?.colors ?? {}),
                            [key]: e.target.value || undefined,
                          },
                        },
                      },
                      path,
                    )
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
